/**
 * Membership Configuration - Native Subscription System
 * Strategic buyer segmentation: Exam Prep Pro vs Academy Career Track
 * 
 * IMPORTANT: Sleep Reset Academy is NOT included in any membership tier.
 * Academy modules are purchased separately - see /academy for module purchases.
 */

export const educationTiers = {
  free: {
    id: 'free',
    name: 'Free',
    level: 0,
    price: 0,
    features: ['Browse website content', 'Preview topic summaries'],
  },
  explorer: {
    id: 'explorer',
    name: 'Explorer',
    level: 1,
    price: 2,
    period: 'month',
    color: 'emerald',
    icon: 'Compass',
    tagline: 'Full access to 200+ sleep topics',
    buyerType: 'General Public',
    primaryValue: 'Full topic library + bilingual content',
    ctaText: 'Start for $2',
    microCopy: 'No commitment. Cancel anytime.',
    bundleValue: 'Foundation for sleep education',
    features: [
      'Full access to ALL 200+ sleep topics',
      'Bilingual content (English & Spanish)',
      'Audio playback (limited preview)',
      'SHS-AI assistant (limited access)',
      'Sleep Coach access (preview)',
    ],
    access: {
      articles: true,
      premium_articles: true,  // Full topic access
      audio_learning: 'limited',  // Limited preview
      ai_topic_preview: true,
      ai_assistant: 'limited',  // Limited access for all tiers
      ai_limit: 5,  // Few prompts per month for taste
      academy: false,
      exams: false,
    },
  },
  deep_sleep: {
    id: 'deep_sleep',
    name: 'Full Learning Access',
    level: 2,
    price: 5,
    period: 'month',
    color: 'blue',
    icon: 'Moon',
    tagline: 'Unlimited topics + audio + Sleep Coach + Wearables',
    buyerType: 'Educated Consumers',
    primaryValue: 'Full sleep learning library + lifestyle tools',
    ctaText: 'Unlock Full Access',
    microCopy: 'Most users upgrade here',
    bundleValue: 'All-in-one sleep learning platform',
    features: [
      'Everything in Explorer',
      'Full access to premium topics',
      'Audio learning experience (unlimited)',
      'Sleep Coach access (full)',
      'Wearable Devices integration',
      'SHS-AI (limited access)',
      'Expanded interactive content',
    ],
    access: {
      articles: true,
      premium_articles: true,
      audio_learning: true,
      ai_topic_preview: true,
      ai_assistant: true,
      ai_limit: 20,
      sleep_coach: true,
      wearables: true,
      academy: false,
      exams: false,
    },
  },
  master_mind: {
    id: 'master_mind',
    name: 'AI Learning Pro',
    level: 3,
    price: 10,
    period: 'month',
    color: 'purple',
    icon: 'Brain',
    popular: true,
    tagline: 'Unlimited SHS-AI + Sleep Coach + Wearables',
    buyerType: 'Power Learners',
    primaryValue: 'Unlimited personalized answers from SHS-AI',
    ctaText: 'Upgrade to AI Pro',
    microCopy: 'Best for interactive learning',
    bundleValue: 'Unlimited AI + Audio + Coach + Wearables',
    features: [
      'Everything in Full Learning Access',
      'Unlimited SHS-AI access',
      'Sleep Coach access (full)',
      'Wearable Devices integration',
      'Advanced learning content',
      'Priority AI support',
    ],
    access: {
      articles: true,
      premium_articles: true,
      audio_learning: true,
      ai_assistant: true,
      ai_limit: -1,
      sleep_coach: true,
      wearables: true,
      academy: false,
      exams: false,
    },
  },
  professional: {
    id: 'professional',
    name: 'Exam Prep Pro',
    level: 4,
    price: 20,
    period: 'month',
    color: 'amber',
    icon: 'Award',
    tagline: 'Advanced training + scoring mastery',
    buyerType: 'Working Technologists',
    primaryValue: 'Train like a sleep technologist',
    ctaText: 'Start Professional Training',
    microCopy: 'For serious learners and technicians',
    bundleValue: 'Complete professional training platform',
    // ITER181 · Exam Prep Pro price changed by Founder to $20/month.
    // Original slot preserved above; canonical price now $20.
    displayPrice: '$20',
    priceValue: 20,
    professionalCredentials: [
      'Used for technician training and QA preparation',
      'Structured professional-level training',
      'Prepare for board exams and clinical accuracy',
    ],
    features: [
      'Full platform access',
      'Question banks + advanced training',
      'Unlimited SHS-AI usage',
      'Exam-prep materials & mock tests',
    ],
    exclusions: [
      'Sleep Reset Academy courses',
      'Technician training curriculum',
    ],
    note: 'This tier is designed for professionals who are already sleep technologists and need structured exam preparation — not for initial technician training.',
    access: {
      articles: true,
      premium_articles: true,
      audio_learning: true,
      ai_assistant: true,
      ai_limit: -1,
      exams: true,
      question_banks: true,
      academy: false,  // EXPLICITLY EXCLUDED - Academy is separate product
      certificates: false,  // Academy certificates only
    },
  },
};

/**
 * Sleep Reset Academy — Career Track
 * SEPARATE PRODUCT - Not included in any membership tier
 * Modules are purchased individually
 */
export const academyConfig = {
  name: 'Sleep Reset Academy — Career Track',
  tagline: 'For future sleep technologists building a professional career',
  buyerType: 'Students, Career Changers, Entry-Level Healthcare Workers',
  description: 'Build your career in sleep medicine with our modular training program. Purchase modules individually and progress at your own pace.',
  features: [
    'Comprehensive technician training curriculum',
    'Module-by-module purchasing',
    'Progress toward full technician certification',
    'Certificates of completion per module',
    'Structured learning paths (CPSGT, RPSGT tracks)',
  ],
  note: 'Academy is designed for future technologists entering the field — NOT for exam-only professionals.',
};

/**
 * Sleep Reset Coach™
 * SEPARATE PRODUCT - Behavioral sleep coaching for general public
 * Independent from membership tiers
 */
export const coachConfig = {
  id: 'sleep_reset_coach',
  name: 'Sleep Reset Coach™',
  price: 2,
  period: 'month',
  tagline: 'Behavioral sleep coaching for better rest',
  buyerType: 'General Public',
  description: 'CBT-I–based behavioral sleep coaching with personalized plans and AI guidance. Improve your sleep naturally without medication.',
  features: [
    'CBT-I–based behavioral sleep coaching',
    'SHS-AI Assistant access (sleep hygiene focus)',
    'Personalized sleep hygiene plans',
    'Sleep diary & habit tracking',
    'Weekly sleep quality assessments',
    'Daily routine recommendations',
  ],
  restrictions: [
    'Educational and behavioral guidance only',
    'Not a medical service',
    'No diagnostic claims',
    'No medication recommendations',
  ],
  disclaimer: 'Educational and behavioral guidance only. Not a medical service. Sleep Reset Coach™ does not diagnose, treat, or provide medical advice. Consult a healthcare provider for medical concerns.',
  color: 'teal',
  icon: 'Heart',
};

/**
 * Product Hierarchy for Pricing Psychology
 */
export const productHierarchy = [
  { id: 'sleep_coach', name: 'Sleep Reset Coach™', price: '$2/mo', purpose: 'Behavioral coaching', buyer: 'General public' },
  { id: 'explorer', name: 'Explorer', price: '$2/mo', purpose: 'Full topic access + SHS-AI preview', buyer: 'General public' },
  { id: 'deep_sleep', name: 'Full Learning Access', price: '$5/mo', purpose: 'Audio + Sleep Coach + Wearables', buyer: 'Educated consumers' },
  { id: 'master_mind', name: 'AI Learning Pro', price: '$10/mo', purpose: 'Unlimited SHS-AI', buyer: 'Power learners' },
  { id: 'professional', name: 'Exam Prep Pro', price: '$20/mo', purpose: 'Exam preparation', buyer: 'Working techs' },
  { id: 'academy', name: 'Sleep Reset Academy', price: 'By module', purpose: 'Career training', buyer: 'Future techs' },
  { id: 'scoring_mastery', name: 'Scoring Mastery™', price: 'Enterprise', purpose: 'Applied QA & accuracy', buyer: 'Hospitals & labs' },
  { id: 'organizations', name: 'Organizations', price: 'Enterprise', purpose: 'Enterprise contracts', buyer: 'Employers' },
  { id: 'research', name: 'Research', price: 'Partnership', purpose: 'Credibility + R&D', buyer: 'Pharma / academia' },
];

// Helper functions
export const getTierById = (tierId) => {
  return educationTiers[tierId] || educationTiers.free;
};

export const getTierLevel = (tierId) => {
  const tier = getTierById(tierId);
  return tier.level || 0;
};

export const hasAccess = (userTier, requiredTier) => {
  const userLevel = getTierLevel(userTier);
  const requiredLevel = getTierLevel(requiredTier);
  return userLevel >= requiredLevel;
};

export const getAccessForTier = (tierId) => {
  const tier = getTierById(tierId);
  return tier.access || { articles: true };
};

/**
 * Check if user has Academy access
 * Academy access comes from purchased modules, NOT from membership tier
 */
export const hasAcademyAccess = (userTier, purchasedModules = []) => {
  // Academy is NEVER included in membership - must purchase modules
  return purchasedModules.length > 0;
};

// Feature to minimum tier mapping
// NOTE: 'academy' is NOT mapped here - it's a separate product
export const featureRequirements = {
  articles: 'free',
  premium_articles: 'explorer',
  audio_learning: 'explorer',
  ai_topic_preview: 'explorer',
  ai_assistant: 'explorer',  // Limited from Explorer; unlimited at master_mind+
  ai_assistant_unlimited: 'master_mind',
  sleep_coach: 'deep_sleep',
  wearables: 'deep_sleep',
  exams: 'professional',
  question_banks: 'professional',
};

export const getRequiredTierForFeature = (feature) => {
  // Academy is not a feature of any tier - it's a separate product
  if (feature === 'academy') {
    return null; // Must purchase separately
  }
  return featureRequirements[feature] || 'professional';
};

// Plan list for display (membership tiers only)
export const planList = Object.values(educationTiers).filter(t => t.id !== 'free');

// Legacy tier mapping (for backwards compatibility during migration)
export const legacyTierMapping = {
  'tier1': 'explorer',
  'tier2': 'deep_sleep',
  'tier3': 'master_mind',
  'tier4': 'professional',
};

export const tierLabelsById = {
  'tier1': 'Explorer',
  'tier2': 'Full Learning Access',
  'tier3': 'AI Learning Pro',
  'tier4': 'Exam Prep Pro',
  'explorer': 'Explorer',
  'deep_sleep': 'Full Learning Access',
  'master_mind': 'AI Learning Pro',
  'professional': 'Exam Prep Pro',
};

export const tierLevelById = {
  'tier1': 1,
  'tier2': 2,
  'tier3': 3,
  'tier4': 4,
  'explorer': 1,
  'deep_sleep': 2,
  'master_mind': 3,
  'professional': 4,
};
